import { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { FilterMode } from '../../types/product';
import MarketingClaimBadge from './MarketingClaimBadge';
import { useTranslation } from '../../i18n';
import { useAuth } from '../../hooks/useAuth';
import {
  AGREEMENT_DOT_CLASSES,
  type AgreementKey,
} from '../../utils/agreementStyles';

export interface FilterOption {
  id: string;
  name: string;
}

const AGREEMENT_IDS = ['DA', 'CPA', 'Gentleman', 'LOI', 'none'] as const;

interface Filters {
  territories: string[];
  applications: string[];
  indications: string[];
  marketingClaims: string[];
  partners: string[];
  categories: string[];
  agreements: string[];
  onlyNew: boolean;
  onlyNewStudy: boolean;
}

export interface FilterModes {
  applications: FilterMode;
  indications: FilterMode;
  marketingClaims: FilterMode;
}

interface FilterOptions {
  territories: FilterOption[];
  applications: FilterOption[];
  indications: FilterOption[];
  marketingClaims: FilterOption[];
  partners: FilterOption[];
  categories: FilterOption[];
}

interface ProductFilterSidebarProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  filterModes: FilterModes;
  onFilterModesChange: (modes: FilterModes) => void;
  filterOptions: FilterOptions;
  isOpen: boolean;
  onToggle: () => void;
}

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  hasActiveFilters?: boolean;
  mode?: FilterMode;
  onModeChange?: (mode: FilterMode) => void;
}

function FilterSection({
  title,
  children,
  defaultOpen = false,
  hasActiveFilters = false,
  mode,
  onModeChange,
}: FilterSectionProps) {
  const [open, setOpen] = useState(defaultOpen || hasActiveFilters);
  const [prevHasActiveFilters, setPrevHasActiveFilters] = useState(hasActiveFilters);

  if (hasActiveFilters !== prevHasActiveFilters) {
    setPrevHasActiveFilters(hasActiveFilters);
    if (hasActiveFilters) setOpen(true);
  }

  return (
    <div className="py-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span>{title}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5">
          {mode && onModeChange && (
            <div className="flex items-center gap-2 pb-2 mb-1 border-b border-gray-100 dark:border-gray-700">
              <span className={`text-xs font-medium transition-colors ${mode === 'AND' ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500'}`}>
                And
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={mode === 'OR'}
                onClick={() => onModeChange(mode === 'AND' ? 'OR' : 'AND')}
                className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-vivatis-600 transition-colors duration-200 cursor-pointer"
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    mode === 'OR' ? 'translate-x-[18px]' : 'translate-x-[3px]'
                  }`}
                />
              </button>
              <span className={`text-xs font-medium transition-colors ${mode === 'OR' ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500'}`}>
                Or
              </span>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

function CheckboxItem({
  label,
  checked,
  onChange,
  icon,
  suffix,
  title,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon?: React.ReactNode;
  suffix?: React.ReactNode;
  title?: string;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer group" title={title}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-vivatis-600 focus:ring-vivatis-500 focus:ring-offset-0 dark:bg-gray-700 cursor-pointer"
      />
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="text-sm text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors truncate">
        {label}
      </span>
      {suffix && <span className="ml-auto flex shrink-0 items-center gap-1">{suffix}</span>}
    </label>
  );
}

export default function ProductFilterSidebar({
  filters,
  onFilterChange,
  filterModes,
  onFilterModesChange,
  filterOptions,
  isOpen,
  onToggle,
}: ProductFilterSidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const restrictedTerritories = user?.territories ?? [];

  const agreementOptions: FilterOption[] = AGREEMENT_IDS.map((id) => ({
    id,
    name: t.filterSidebar.agreements[id],
  })).sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));

  const toggleFilter = (
    key: keyof Filters,
    value: string,
    checked: boolean
  ) => {
    const current = filters[key];
    if (typeof current === 'boolean') return;
    const updated = checked
      ? [...current, value]
      : current.filter((v) => v !== value);
    onFilterChange({ ...filters, [key]: updated });
  };

  const clearAll = () => {
    onFilterChange({
      territories: [],
      applications: [],
      indications: [],
      marketingClaims: [],
      partners: [],
      categories: [],
      agreements: [],
      onlyNew: false,
      onlyNewStudy: false,
    });
  };

  const activeFilterCount =
    filters.territories.length +
    filters.applications.length +
    filters.indications.length +
    filters.marketingClaims.length +
    filters.partners.length +
    filters.categories.length +
    filters.agreements.length +
    (filters.onlyNew ? 1 : 0) +
    (filters.onlyNewStudy ? 1 : 0);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-dvh w-72 transform transition-transform duration-300 lg:sticky lg:top-20 lg:z-auto lg:translate-x-0 lg:max-h-[calc(100dvh-5.5rem)] ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-white dark:bg-gray-800/80 lg:rounded-2xl overflow-y-auto pb-8`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Filter
          </h2>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              >
                Clear all
              </button>
            )}
            <button
              type="button"
              onClick={onToggle}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors lg:hidden"
            >
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Quick filters */}
        <div className="px-4 py-3 space-y-1.5 border-b border-gray-100 dark:border-gray-700">
          <CheckboxItem
            label={t.filterSidebar.newProducts}
            checked={filters.onlyNew}
            onChange={(checked) => onFilterChange({ ...filters, onlyNew: checked })}
          />
          <CheckboxItem
            label={t.filterSidebar.newStudy}
            checked={filters.onlyNewStudy}
            onChange={(checked) => onFilterChange({ ...filters, onlyNewStudy: checked })}
          />
        </div>

        {/* Filter Sections */}
        <FilterSection title={t.filterSidebar.category} hasActiveFilters={filters.categories.length > 0}>
          {filterOptions.categories.map((category) => (
            <CheckboxItem
              key={category.id}
              label={category.name}
              checked={filters.categories.includes(category.id)}
              onChange={(checked) =>
                toggleFilter('categories', category.id, checked)
              }
            />
          ))}
        </FilterSection>

        <FilterSection title={t.filterSidebar.country} hasActiveFilters={filters.territories.length > 0}>
          {filterOptions.territories.map((territory) => (
            <CheckboxItem
              key={territory.id}
              label={territory.name}
              checked={filters.territories.includes(territory.id)}
              onChange={(checked) =>
                toggleFilter('territories', territory.id, checked)
              }
            />
          ))}
        </FilterSection>
        {restrictedTerritories.length > 0 && (
          <p className="px-4 pb-3 text-xs text-gray-500 dark:text-gray-400">
            {t.filterSidebar.restrictedTo(restrictedTerritories.map((territory) => territory.code).join(', '))}
          </p>
        )}

        <FilterSection
          title={t.filterSidebar.application}
          hasActiveFilters={filters.applications.length > 0}
          mode={filterModes.applications}
          onModeChange={(m) => onFilterModesChange({ ...filterModes, applications: m })}
        >
          {filterOptions.applications.map((application) => (
            <CheckboxItem
              key={application.id}
              label={application.name}
              checked={filters.applications.includes(application.id)}
              onChange={(checked) =>
                toggleFilter('applications', application.id, checked)
              }
            />
          ))}
        </FilterSection>

        <FilterSection
          title={t.filterSidebar.indication}
          hasActiveFilters={filters.indications.length > 0}
          mode={filterModes.indications}
          onModeChange={(m) => onFilterModesChange({ ...filterModes, indications: m })}
        >
          {filterOptions.indications.map((indication) => (
            <CheckboxItem
              key={indication.id}
              label={indication.name}
              checked={filters.indications.includes(indication.id)}
              onChange={(checked) =>
                toggleFilter('indications', indication.id, checked)
              }
            />
          ))}
        </FilterSection>

        <FilterSection title={t.filterSidebar.partner} hasActiveFilters={filters.partners.length > 0}>
          {filterOptions.partners.map((partner) => (
            <CheckboxItem
              key={partner.id}
              label={partner.name}
              checked={filters.partners.includes(partner.id)}
              onChange={(checked) =>
                toggleFilter('partners', partner.id, checked)
              }
            />
          ))}
        </FilterSection>

        <FilterSection title={t.filterSidebar.agreement} hasActiveFilters={filters.agreements.length > 0}>
          {agreementOptions.map((option) => {
            const key = option.id as AgreementKey;
            return (
              <CheckboxItem
                key={option.id}
                label={option.name}
                checked={filters.agreements.includes(option.id)}
                onChange={(checked) =>
                  toggleFilter('agreements', option.id, checked)
                }
                icon={
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${AGREEMENT_DOT_CLASSES[key]}`}
                    aria-hidden
                  />
                }
                title={t.filterSidebar.agreementDescriptions[key]}
              />
            );
          })}
        </FilterSection>

        <FilterSection
          title={t.filterSidebar.marketingClaims}
          hasActiveFilters={filters.marketingClaims.length > 0}
          mode={filterModes.marketingClaims}
          onModeChange={(m) => onFilterModesChange({ ...filterModes, marketingClaims: m })}
        >
          {filterOptions.marketingClaims.map((claim) => (
            <CheckboxItem
              key={claim.id}
              label={claim.name}
              checked={filters.marketingClaims.includes(claim.id)}
              onChange={(checked) =>
                toggleFilter('marketingClaims', claim.id, checked)
              }
              icon={<MarketingClaimBadge claim={claim} />}
            />
          ))}
        </FilterSection>
      </aside>
    </>
  );
}
